// Vendor popularity data used by the vendor inventory onboarding flows. Snapshot of adoption counts (how many
// orgs have added each catalog vendor), matched to the catalog by exact name. If the catalog names change, or a
// popularity signal is added to the catalog API, update or replace this.

// The most-adopted catalog vendors, in adoption order. Used to rank Add Vendor dropdown results so the most
// likely picks surface first; unlisted vendors sort alphabetically after these.
export const VENDOR_POPULARITY_RANKING = [
  "Google Analytics",
  "Meta Ads",
  "Google Ads",
  "Shopify",
  "PayPal - Pay with PayPal, Venmo, Pay Later",
  "Klaviyo",
  "Microsoft Ads",
  "Tiktok Ads",
  "UPS",
  "Google Workspace",
  "Shop Pay",
  "FedEx",
  "Amazon Web Services",
  "Apple Pay",
  "Gorgias",
  "Pinterest Ads",
  "USPS",
  "Stripe",
  "Avalara AvaTax",
  "Cloudflare",
  "Google Pay",
  "Attentive",
  "Microsoft 365",
  "ShipStation",
  "Zendesk",
  "Slack",
  "Microsoft Clarity",
  "Amazon Pay",
];

export const vendorPopularityRank = (name: string): number => {
  const idx = VENDOR_POPULARITY_RANKING.indexOf(name);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

// A breadth-balanced subset of the ranking (~2 per category), surfaced as the first-run "quick add" so users can
// add several common tools at once. Deliberately excludes TrueVault (auto-added to every org) and Shopify Network
// Intelligence (adding it has survey side-effects that reload the page — unsuitable for a bulk add).
export const QUICK_ADD_VENDOR_NAMES: string[] = [
  "Google Analytics",
  "Meta Ads",
  "Google Ads",
  "Shopify",
  "PayPal - Pay with PayPal, Venmo, Pay Later",
  "Stripe",
  "Klaviyo",
  "Attentive",
  "UPS",
  "FedEx",
  "Google Workspace",
  "Avalara AvaTax",
  "Amazon Web Services",
  "Gorgias",
  "Zendesk",
  "Cloudflare",
  "Slack",
  "Microsoft Clarity",
];

// Friendlier display labels where the catalog name is verbose.
export const VENDOR_DISPLAY_LABELS: Record<string, string> = {
  "PayPal - Pay with PayPal, Venmo, Pay Later": "PayPal",
};

// Vendor categories in popularity order, derived from VENDOR_POPULARITY_RANKING (a category ranks by its
// best-ranked vendor's catalog category). Drives the "add vendors by category" chips: ranked categories sort
// first and render as recommended; unranked ones follow alphabetically with the default look.
export const CATEGORY_POPULARITY_RANKING = [
  "Data Analytics Provider",
  "Ad Network",
  "Commerce Software Tool",
  "Payment Processor",
  "Sales & Marketing Tool",
  "Shipping Service",
  "Business Operations Tool",
  "IT Infrastructure Service",
  "Customer Support Tool",
  "Cybersecurity Provider",
  "Collaboration & Productivity Tool",
];

export const categoryPopularityRank = (category: string): number => {
  const idx = CATEGORY_POPULARITY_RANKING.indexOf(category);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

export const isPopularCategory = (category: string): boolean =>
  CATEGORY_POPULARITY_RANKING.includes(category);
